import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { globalStyle } from "../../global/Utils";
import {
  getStockMovements,
  clearStockMovements,
} from "../../Actions/StockAction";
import {
  MOVEMENT_TYPE_LABEL,
  formatQty,
  stockChipStyle,
} from "../../global/stock";

/**
 * The ledger behind one item's quantity.
 *
 * This is the point of holding stock as movements rather than a column: every
 * change reads back with its date, its sign and the document that caused it,
 * and the rows add up to what the item says it has.
 */
const StockMovementsModal = ({ open, item, onClose }) => {
  const dispatch = useDispatch();

  const {
    stockMovements,
    movementsItem,
    isGettingStockMovements,
    stockMovementsError,
  } = useSelector((state) => state.StockReducer);

  useEffect(() => {
    if (open && item?._id) {
      // Page 0 with a generous limit: this is a reading view, not a list that
      // needs paging controls of its own.
      dispatch(getStockMovements(item._id, 0, 100));
    }
    if (!open) {
      dispatch(clearStockMovements());
    }
  }, [open, item, dispatch]);

  const unit = movementsItem?.unit || item?.unit || "";
  const currentStock = movementsItem?.currentStock ?? item?.currentStock ?? 0;
  const state = movementsItem?.stockState || item?.stockState;
  const chip = state ? stockChipStyle(state) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontFamily: globalStyle.fontFamilyHeading }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <span>{item?.name || "Stock History"}</span>
          <Box display="flex" alignItems="center" gap={1}>
            <b>{formatQty(currentStock, unit)}</b>
            {chip && (
              <span
                style={{
                  backgroundColor: chip.bg,
                  color: chip.fg,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "2px 10px",
                  borderRadius: 10,
                }}
              >
                {chip.label}
              </span>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isGettingStockMovements ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={34} />
          </Box>
        ) : stockMovementsError ? (
          <Box py={4} textAlign="center" color="error.main">
            {stockMovementsError}
          </Box>
        ) : stockMovements.length === 0 ? (
          <Box py={4} textAlign="center" color="text.secondary">
            No movements yet. Stock changes appear here as soon as this item is
            received or sold.
          </Box>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  style={{
                    background: globalStyle.blueColor,
                    color: "#fff",
                  }}
                >
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Reference</th>
                  <th className="text-right px-3 py-2">Balance</th>
                </tr>
              </thead>
              <tbody>
                {stockMovements.map((movement) => {
                  const positive = movement.quantity >= 0;
                  return (
                    <tr key={movement._id} className="border-b">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {movement.date ||
                          (movement.createdAt
                            ? moment(movement.createdAt).format("DD MMM YYYY")
                            : "-")}
                      </td>
                      <td className="px-3 py-2">
                        {MOVEMENT_TYPE_LABEL[movement.type] || movement.type}
                      </td>
                      <td
                        className="px-3 py-2 text-right font-bold"
                        style={{ color: positive ? "#1E7D34" : "#C62828" }}
                      >
                        {positive ? "+" : ""}
                        {formatQty(movement.quantity)}
                      </td>
                      <td className="px-3 py-2">
                        {movement.referenceLabel ||
                          movement.referenceType ||
                          "-"}
                        {movement.note ? (
                          <div className="text-xs text-gray-500 italic">
                            {movement.note}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {/* Only the first page can compute a running balance
                            without the rows above it, so the column is blank
                            rather than quietly wrong. */}
                        {movement.balanceAfter === null ||
                        movement.balanceAfter === undefined
                          ? "-"
                          : formatQty(movement.balanceAfter)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockMovementsModal;
